import {MissionService, AuthenticationError, ResourceNotFoundError} from '@/services/mission.service';
import {StorageService} from "../services/storage.service";
import {UserService} from '@/services/user.service';

const state = {
    mission: {},
    missions: [],
    totalMissions: 0,
    mission_detail: {},
    errorCode: 0,
    errorMessage: '',
    lastAppliedMissionId: '',
    withoutApplications: false,
    sort: 'arrival_date',

    pageCount: 25,
    page: 0,

    isDetailsLoading: false,
    isLoading: false,

    savedFilters: StorageService.get('filterOptions') ?? {},
    filter: {},

    isAnonymized: false,
    anonymized_availability_date: '',
};

const getters = {
    GET_ANONYMIZED_AVAILABILITY_DATE(state) {
        if (state.anonymized_availability_date === '')
            return new Date().toISOString().slice(0, 10);
        else
            return state.anonymized_availability_date;
    },
    withoutApplications(state) {
        return state.withoutApplications;
    }
};

const actions = {
    removeMission: function ({commit, state}, mission_id) {

        const oldTotalCount = state.missions.length;

        const missions = state.missions.filter((mission) => {
            return mission.mission_id !== mission_id;
        });

        const difference = oldTotalCount - missions.length;

        commit('setMissions', missions);
        commit('setTotalMissions', state.totalMissions - difference);
    },
    missions: async function ({commit}) {
        commit('LOADING_START');
        commit('setAnonymized', {
            isAnonymized: false
        });
        try {
            const result = await MissionService.missions(
                state.withoutApplications,
                state.pageCount,
                state.page,
                state.sort
            );
            commit('addMissions', result.missions);
            commit('setTotalMissions', result.total);
            return true;
        } catch (e) {
            if (e instanceof AuthenticationError) {
                commit('setError', {errorCode: e.errorCode, errorMessage: e.message});
            }

            return false
        } finally {
            commit('LOADING_END');
        }
    },

    anonymizedMissions: async function ({commit, getters}) {
        commit('LOADING_START');
        commit('setAnonymized', {
            isAnonymized: true
        });

        let parsedFilters = {};
        let date = getters.GET_ANONYMIZED_AVAILABILITY_DATE;
        try {
            state.savedFilters = StorageService.get('filterOptions');
            parsedFilters = JSON.parse(state.savedFilters);
            if (!date) {
                date = parsedFilters.availability_date;
            }
        } catch {
            parsedFilters = {};
        }

        try {
            const result = await MissionService.anonymizedMissions(
                date,
                parsedFilters,
                this.$app.$i18n.locale,
                state.withoutApplications,
                state.pageCount,
                state.page,
                state.sort);

            commit('addMissions', result.missions);
            commit('setTotalMissions', result.total);
            return true;
        } catch (e) {
            console.log(e);
            if (e instanceof AuthenticationError) {
                commit('setError', {errorCode: e.errorCode, errorMessage: e.message});
            }

            return false
        } finally {
            commit('LOADING_END');
        }
    },

    setFilter({commit}, filters) {
        commit('SET_FILTERS', filters);
    },

    setSort({commit}, sort) {
        commit('setSort', sort);
    },

    async details({commit}, mission_id) {
        commit('setDetails', {});

        commit('LOADING_DETAILS_START');
        try {
            const details = await MissionService.details(mission_id);
            commit('setDetails', details);
            return details;
        } catch (e) {
            if (e instanceof AuthenticationError) {
                commit('setError', {errorCode: e.errorCode, errorMessage: e.message});
            }

            if (e instanceof ResourceNotFoundError) {
                commit('setError', {errorCode: e.errorCode, errorMessage: e.message});
            }
            return null;
        } finally {
            commit('LOADING_DETAILS_END');
        }
    },
    incrementPage({commit, state}) {
        const page = state.page + 1;
        commit('setPage', page);
    },
    resetSearch({commit}) {
        commit('setPage', 0);
        commit('resetMissions');
    },

    async filter({commit}) {
        try {
            const result = await UserService.getFilter();
            commit('SET_FILTERS', result.filter);
        } catch (e) {
            commit('SET_FILTERS', {});
        }
    },

    async anonymisedFilter({commit}) {
        try {
            const result = await UserService.getAnonymisedFilter(this.$app.$i18n.locale);
            const anonymisedFilter = JSON.parse(StorageService.get('filterOptions'));
            if (anonymisedFilter) {
                result.filter = anonymisedFilter;
            }
            commit('SET_FILTERS', result.filter);
        } catch (e) {
            commit('SET_FILTERS', {});
        }
    },
};

const mutations = {
    SET_FILTERS(state, filter) {
        StorageService.save('filterOptions', JSON.stringify(filter));
        state.filter = filter;
    },
    setSort(state, sort) {
        state.sort = sort;
    },
    setTotalMissions(state, total) {
        state.totalMissions = total;
    },
    updateWithoutApplicationsFilter(state, value) {
        state.withoutApplications = value;
    },
    setAnonymized(state, payload) {
        state.isAnonymized = payload.isAnonymized;
    },

    SET_ANONYMIZED_AVAILABILITY_DATE(state, date) {
        state.anonymized_availability_date = date;
    },

    resetMissions() {
        state.missions = [];
    },

    addMissions(state, missions) {
        missions.forEach(mission => {
            state.missions.push(mission);
        });
    },

    setMissions(state, missions) {
        state.missions = missions;
    },

    LOADING_START(state) {
        state.isLoading = true;
    },

    LOADING_END(state) {
        state.isLoading = false;
    },

    LOADING_DETAILS_START(state) {
        state.isDetailsLoading = true;
    },

    LOADING_DETAILS_END(state) {
        state.isDetailsLoading = false;
    },

    setMission(state, mission) {
        state.mission = mission;
    },

    setDetails(state, details) {
        state.mission_detail = details;
    },

    setError(state, error) {
        state.errorCode = error.errorCode;
        state.errorMessage = error.errorMessage;
    },

    lastAppliedMission(state, mission_id) {
        state.lastAppliedMissionId = mission_id;
    },
    setPage(state, page) {
        state.page = page;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}